// Packages
import phoneNumber from 'awesome-phonenumber';

// Services
import { toSlug } from 'utils/string';
import { arrayLoop, arrayIncludes } from 'utils/array';

// Models
import moment from 'moment';

export const separator = '|';

export const getFormData = (id, fields, args = {}) => {
  const view = {};
  const model = {};
  const errors = {};
  let dirty = false;
  let valid = true;

  arrayLoop(fields, (fieldId, field) => {
    const formId = fieldId.split(separator)[0];

    if (id !== formId || !field || field.destroyed) return false;

    if (args.removeFormId) fieldId = fieldId.split(separator)[1];

    view[fieldId] = field.view;
    model[fieldId] = field.model;
    errors[fieldId] = field.serverError || field.error;

    if (field.dirty) dirty = true;
    if (field.error) valid = false;
  });

  return {
    view,
    model,
    errors,
    dirty,
    valid,
  };
};

export const isUrl = url =>
  // eslint-disable-next-line no-useless-escape
  !!/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
    url || ''
  );

export const hasUrlProtocol = url => !!/^http(s?):\/\//i.test(url || '');

export const isEmail = email =>
  !!/^[a-zA-Z0-9!#.$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(
    email
  );

export const browserSupportsInputDate = () => {
  const input = document.createElement('input');
  const notADateValue = 'not-a-date';

  input.setAttribute('type', 'date');
  input.setAttribute('value', notADateValue);

  return input.value !== notADateValue;
};

export const modelToView = (model = '', { type, options }) => {
  if (type === 'text') return model;
  if (type === 'number') return model ? parseInt(model, 10) : '';
  if (type === 'boolean') return model ? true : model === 'null' ? null : false;
  if (type === 'url') return hasUrlProtocol(model) ? model : `https://${model}`;
  // if (type === `date`) return formatDate(model);
  if (type === 'tel') return phoneNumber(model, 'US').getNumber();

  if (type === 'email') return model.toLowerCase();

  if (type === 'file') return model.name || '';

  if (type === 'select' || type === 'radio') {
    if (model === '-1' || model === '') return undefined;
    const optionsChecked = options[model] || {};

    return modelToView(optionsChecked.value, {
      type: optionsChecked.type,
    });
  }

  if (type === 'checkbox') {
    const view = [];
    arrayLoop(model || {}, (k, v) => {
      if (!v) return;

      const option = options[k];
      view.push(
        modelToView(option.value, {
          type: option.type,
        })
      );
    });
    return view;
  }

  return model;
};

export const hasError = (id, field) => {
  if (!field) return false;

  const value = field.view;
  const model = field.model || '';

  const hasValue = typeof value !== 'undefined';

  const type = field.type;

  const changePassword = id.split('|')[1] === 'current_password';

  const isRegisterBookingInitial = id === 'auth-registerbooking|mdlive_consent_user_initials';

  const isType = str => type === str;

  const min = typeof field.min === 'undefined' ? null : field.min;
  const max = typeof field.max === 'undefined' ? null : field.max;

  // Tel
  if (type === 'tel' && model && !phoneNumber(model, 'US').isValid())
    return 'tel';

  // Required
  if (field.required) {
    if (isType('select') && typeof value === 'undefined') return 'required';
    else if (isType('checkbox') && (value || []).length <= 0) return 'required';
    else if (isType('number') && !value)
      return value === 0 ? false : 'required';
    else if (!isType('select') && !value) return 'required';
  } else if (!hasValue) {
    return null;
  }

  // Text
  const isText =
    isType('text') ||
    isType('textarea') ||
    (isType('password') && !changePassword);
  if (isText && min !== null && value && value.length < min) return 'min-text';
  if (isText && max !== null && value && value.length > max) return 'max-text';

  //check user initials
  if (
    isText && isRegisterBookingInitial) {
    const firstName = JSON.parse(localStorage.getItem('updateUser')).first_name;
    const lastName = JSON.parse(localStorage.getItem('updateUser')).last_name;
    if (value.toLowerCase() !== (firstName[0] + lastName[0]).toLowerCase()) return 'initials-match';
  }
  // Password
  const badPasswords = [
    'qwerty',
    'abc123',
    'password',
    'password1',
    'password123',
    '123456',
    '111111',
    '123123',
    'letmein',
  ];

  const hasNumber = /\d/;
  const hasLowercase = /[a-z]/;
  const hasUppercase = /[A-Z]/;
  const hasSpecial = /[!#$~]/;

  const currentUrl = window.location.href;
  const isLogin = currentUrl.includes('log-in');

  if (!changePassword) {
    if (
      type === 'password' &&
      !isLogin &&
      hasValue &&
      arrayIncludes(badPasswords, value)
    )
      return 'bad-password';
    if (type === 'password' && !isLogin && !hasNumber.test(value))
      return 'needs-number';
    if (type === 'password' && !isLogin && !hasLowercase.test(value))
      return 'needs-lowercase';
    if (type === 'password' && !isLogin && !hasUppercase.test(value))
      return 'needs-uppercase';
    if (type === 'password' && !isLogin && !hasSpecial.test(value))
      return 'needs-special';
  }

  // Email
  if (type === 'email' && hasValue && !isEmail(value)) return 'email';

  // URL
  if (type === 'url' && hasValue && !isUrl(value)) return 'url';

  // Number
  // NOTE: Checking the amount of characters, not the number itself
  if (isType('number') && min !== null && String(value).length < min)
    return 'min-number';
  if (isType('number') && max !== null && String(value).length > max)
    return 'max-number';

  // Checkbox
  if (isType('checkbox') && min !== null && (value || []).length < min)
    return min === 1 ? 'required' : 'min-options';
  if (isType('checkbox') && max !== null && (value || []).length > max)
    return 'max-options';

  if (type === 'date') {
    const splitDate = value.split('/');

    if (splitDate[2] > moment().year() && field.allowFuture === null) {
      return 'date-in-future';
    }

    if (parseInt(splitDate[2]) < moment().year() - 100) {
      return 'to-old';
    }

    if (moment().diff(moment(value), 'years') < 18 && min >= 18) {
      return 'to-young';
    }
  }

  return null;
};

export const formatError = (error, { min, max }) => {
  const errors = {
    required: 'This field is required',
    email: 'Invalid email',
    'email-lowercase': 'Email cannot contain uppercase characters',
    url: 'Invalid URL',

    'min-number': `Field must container ${min} numbers`,
    'max-number': `Field cannot container more than ${min} numbers`,
    ///////////

    'min-text': `${min} character(s) min.`,
    'max-text': `${max} character(s) max.`,
    'min-options': `Select a minimum of ${min}`,
    'max-options': `Select a maximum of ${max}`,
    'bad-password': 'Use a more secure password',
    tel: 'Invalid phone number',
    'tel-prefix': 'Invalid country code',
    matches: 'Fields do not match',
    'date-in-future': 'The date cannot be in the future',
    'to-old': 'The date cannot be this far in the past',
    'to-young': 'You must be at least 18 years old',
    'needs-number': 'Password must contain at least one number',
    'needs-uppercase': 'Password must contain at least one uppercase letter',
    'needs-lowercase': 'Password must contain at least one lowercase letter',
    'needs-special': 'Password must contain a special characters (! # $ ~)',
    'initials-match': 'Initials do not match',
  };

  return errors[error] || 'Something went wrong';
};

export const createFieldOption = (label, value, type) => {
  if (typeof value === 'undefined') {
    return {
      label,
      value: toSlug(label),
    };
  }

  return {
    label,
    value,
    type: type || 'text',
  };
};



// WEBPACK FOOTER //
// ./client/utils/forms.js